import React, { useState } from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSnackbar } from "notistack";
import { formatDate1 } from "../../../common/util";
import { parseTime } from "../../../helpers/UtilityHelper";
import { useAppointmentConfig, getReasonLabel, getReasonCategoryLabel } from "./useAppointmentConfig";
import { getClosureCategoryMeta } from "./constants";
import { HISTORY_STATUS_STYLE as STATUS_STYLE } from "./tokens";
import { apiConstants, getGeneric } from "../../../common/constants";
import { categoryLabel } from "../../B2BOrder/ExtraDocsEditor";
import { getMoveTag, summarizeChanges } from "./editLog";
import EditDocLinks from "./EditDocLinks";

const DOC_BUTTONS = [
    { type: "INVOICE", keyField: "appointment_del_invoice_s3_key", label: "Invoice", Icon: ReceiptLongIcon, color: "#1565c0", bg: "#e3f2fd" },
    { type: "PO_COPY", keyField: "appointment_del_po_copy_s3_key", label: "PO Copy", Icon: DescriptionIcon, color: "#2e7d32", bg: "#e8f5e9" },
    { type: "EWAY_BILL", keyField: "appointment_del_eway_bill_s3_key", label: "E-way Bill", Icon: LocalShippingIcon, color: "#e65100", bg: "#fff3e0" },
];

function DocButtons({ entry, awbNumber, historyIndex }) {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState("");

    const open = async (docType) => {
        setLoading(docType);
        try {
            const params = { awbNumber, docType };
            if (historyIndex != null) params.historyIndex = historyIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) {
                window.open(url, "_blank", "noopener");
            } else {
                enqueueSnackbar("Document URL missing", { variant: "warning" });
            }
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
        setLoading("");
    };

    const openById = async (docId) => {
        setLoading(docId);
        try {
            const params = { awbNumber, docId };
            if (historyIndex != null) params.historyIndex = historyIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) {
                window.open(url, "_blank", "noopener");
            } else {
                enqueueSnackbar("Document URL missing", { variant: "warning" });
            }
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
        setLoading("");
    };

    const available = DOC_BUTTONS.filter((d) => !!entry?.[d.keyField]);
    const extras = Array.isArray(entry?.appointment_del_extra_docs) ? entry.appointment_del_extra_docs : [];
    if (available.length === 0 && extras.length === 0) return null;
    return (
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
            {available.map(({ type, label, Icon, color, bg }) => (
                <Tooltip key={type} title={`View ${label}`}>
                    <IconButton
                        size="small"
                        onClick={() => open(type)}
                        disabled={loading === type}
                        sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: bg, color, "&:hover": { bgcolor: bg, filter: "brightness(0.95)" } }}
                    >
                        <Icon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            ))}
            {extras.map((xd) => (
                <Tooltip key={xd.docId} title={`View ${xd.docLabel || categoryLabel(xd.docType)}`}>
                    <IconButton
                        size="small"
                        onClick={() => openById(xd.docId)}
                        disabled={loading === xd.docId}
                        sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: "#ede7f6", color: "#5e35b1", "&:hover": { bgcolor: "#ede7f6", filter: "brightness(0.95)" } }}
                    >
                        <DescriptionIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            ))}
        </Box>
    );
}

function HistoryCard({ attemptNum, status, dateLine, timeLine, po, reasonLine, attributionLabel, attributionColor, metaLine, docs, edits, awbNumber, historyIndex }) {
    const style = STATUS_STYLE[status] || STATUS_STYLE.MISSED;
    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                p: 1.25,
                borderRadius: 1,
                mb: 0.75,
                borderLeft: `3px solid ${style.borderColor}`,
                bgcolor: style.bg,
            }}
        >
            <Box sx={{ minWidth: 130, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Typography variant="caption" fontWeight={700}>Appt #{attemptNum}</Typography>
                <Chip size="small" label={status} color={style.chipColor} sx={{ height: 18, fontSize: 10 }} />
                {Array.isArray(edits) && edits.length > 0 && (
                    <Chip size="small" label="EDITED" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "#f3e5f5", color: "#7b1fa2" }} />
                )}
            </Box>
            <Box sx={{ flex: 1, fontSize: 12, color: "#555" }}>
                <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
                    <strong>{dateLine}</strong>
                    {timeLine ? ` · ${timeLine}` : ""}
                    {po ? ` · PO: ${po}` : ""}
                </Typography>
                {reasonLine && (
                    <Typography variant="caption" sx={{ display: "block", color: attributionColor || "#e65100", mt: 0.25 }}>
                        <strong>{attributionLabel ? `${attributionLabel}: ` : ""}</strong>{reasonLine}
                    </Typography>
                )}
                {metaLine && (
                    <Typography variant="caption" sx={{ display: "block", color: "#888", mt: 0.25, fontSize: 11 }}>
                        {metaLine}
                    </Typography>
                )}
                {docs}
                {Array.isArray(edits) && edits.length > 0 && (
                    <Box sx={{ mt: 1, pt: 0.75, borderTop: "1px dashed #d1c4e9" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#5e35b1", display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <EditOutlinedIcon sx={{ fontSize: 13 }} /> Edit Log ({edits.length})
                        </Typography>
                        <Stack spacing={0.5}>
                            {edits.map((edit, i) => {
                                const moveTag = getMoveTag(edit.changes);
                                const changesSummary = [moveTag?.label, ...summarizeChanges(edit.changes).map((l) => `${l.label}: ${l.text}`)]
                                    .filter(Boolean).join(" · ");
                                const timeStr = edit.edited_at ? formatDate1(edit.edited_at, "dd MMM, yyyy HH:mm") : "";
                                return (
                                    <Box key={i} sx={{ bgcolor: "#fbf9fe", p: 0.75, borderRadius: 0.75, border: "1px solid #ede7f6", fontSize: 11 }}>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, color: "#333" }}>
                                            <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: "#5e35b1" }}>
                                                Edited by {edit.edited_by || edit.edited_by_email || "Ops"}
                                            </Typography>
                                            {timeStr && <Typography component="span" sx={{ fontSize: 11, color: "#777" }}>at {timeStr}</Typography>}
                                            {(edit.reason_label || edit.reason_code) && (
                                                <Typography component="span" sx={{ fontSize: 11, color: "#e65100", fontWeight: 500 }}>
                                                    · Reason: {edit.reason_label || edit.reason_code}
                                                    {edit.reason_remarks ? ` ("${edit.reason_remarks}")` : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                        {changesSummary && (
                                            <Typography sx={{ fontSize: 11, color: "#555", mt: 0.25, fontStyle: "italic" }}>
                                                {changesSummary}
                                            </Typography>
                                        )}
                                        <EditDocLinks changes={edit.changes} awbNumber={awbNumber}
                                            historyIndex={historyIndex} editIndex={i} sx={{ mt: 0.25 }} />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function AppointmentHistoryView({ rowData }) {
    const { config: apptConfig } = useAppointmentConfig();
    const history = rowData?.appointmentDeliveryHistory || [];
    const histCount = rowData?.appointmentDeliveryHistoryCount ?? history.length;
    const hasActive = !!(rowData?.appointmentDelivery && rowData?.appointmentDeliveryData);
    const active = hasActive ? rowData.appointmentDeliveryData : null;
    const totalAttempts = histCount + (hasActive ? 1 : 0);
    const awbNumber = rowData?.awb_number;

    if (totalAttempts === 0) {
        return <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>No appointment attempts yet.</Typography>;
    }

    const activeAttemptNum = histCount + 1;

    return (
        <Box sx={{ p: 1.5, bgcolor: "#f8f9ff", borderRadius: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Appointment History — {totalAttempts} attempt{totalAttempts > 1 ? "s" : ""}
            </Typography>
            <Stack spacing={0}>
                {hasActive && active && (
                    <HistoryCard
                        attemptNum={activeAttemptNum}
                        status="ACTIVE"
                        dateLine={active.appointment_del_start ? formatDate1(active.appointment_del_start, "dd MMM, yyyy") : "-"}
                        timeLine={active.appointment_del_start && active.appointment_del_end
                            ? `${parseTime(active.appointment_del_start)} - ${parseTime(active.appointment_del_end)}`
                            : ""}
                        po={active.appointment_del_po || active.appointment_del_po_number || ""}
                        metaLine={active.created_at ? `Scheduled on ${formatDate1(active.created_at, "dd MMM, yyyy HH:mm")}` : ""}
                        docs={<DocButtons entry={active} awbNumber={awbNumber} />}
                        edits={active.appointment_del_edits}
                        awbNumber={awbNumber}
                    />
                )}
                {[...history].reverse().map((h, revIdx) => {
                    const idx = history.length - 1 - revIdx;
                    const reasonLabel = getReasonLabel(apptConfig, h.appointment_closure_reason);
                    const catMeta = getClosureCategoryMeta(h.appointment_closure_category);
                    const catLabel = getReasonCategoryLabel(apptConfig, h.appointment_closure_reason) || catMeta?.label || '';
                    const attribColor = catMeta?.color || "";
                    const remarks = h.appointment_closure_remarks ? ` · "${h.appointment_closure_remarks}"` : "";
                    const markedBy = h.appointment_closed_by ? `Marked by ${h.appointment_closed_by}` : "";
                    const markedAt = h.appointment_closed_at ? ` on ${formatDate1(h.appointment_closed_at, "dd MMM, yyyy HH:mm")}` : "";
                    return (
                        <HistoryCard
                            key={idx}
                            attemptNum={h.attemptNumber || (idx + 1)}
                            status="MISSED"
                            dateLine={h.appointment_del_start ? formatDate1(h.appointment_del_start, "dd MMM, yyyy") : "-"}
                            timeLine={h.appointment_del_start && h.appointment_del_end
                                ? `${parseTime(h.appointment_del_start)} - ${parseTime(h.appointment_del_end)}`
                                : ""}
                            po={h.appointment_del_po || h.appointment_del_po_number || ""}
                            reasonLine={reasonLabel || ""}
                            attributionLabel={catLabel || ""}
                            attributionColor={attribColor}
                            metaLine={`${markedBy}${markedAt}${remarks}`.trim() || ""}
                            docs={<DocButtons entry={h} awbNumber={awbNumber} historyIndex={idx} />}
                            edits={h.appointment_del_edits}
                            awbNumber={awbNumber}
                            historyIndex={idx}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
}

export default AppointmentHistoryView;
